import React, { useEffect, useState, useRef } from 'react';
import { ExternalLink, Bookmark } from 'lucide-react';
import { documentApi, projectsApi, Document } from '@/services/api';
import { useProjects, refreshProjectsCache } from '@/hooks/useProjects';
import MarkdownMessage from '@/components/MarkdownMessage';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext';
import { cn } from '@/lib/utils';

interface DocumentViewProps {
  documentId: string;
  onTitleLoaded?: (title: string) => void;
}

export function DocumentView({ documentId, onTitleLoaded }: DocumentViewProps) {
  const { toast } = useToast();
  const { setBreadcrumbActions, headerCompact } = useBreadcrumbLabel();
  const [document, setDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const { projects, loading: projectsLoading } = useProjects();
  const [createName, setCreateName] = useState('');
  const [saving, setSaving] = useState(false);
  const onTitleLoadedRef = useRef(onTitleLoaded);
  onTitleLoadedRef.current = onTitleLoaded;

  useEffect(() => {
    setLoading(true);
    documentApi
      .getDocument(documentId)
      .then((doc) => {
        setDocument(doc);
        onTitleLoadedRef.current?.(doc.title);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [documentId]);

  const openSaveDialog = () => {
    setSaveDialogOpen(true);
    setCreateName('');
  };

  const handleSaveToProject = async (projectId: string, projectName?: string) => {
    if (!document) return;
    setSaving(true);
    try {
      await projectsApi.addDocument(projectId, document.id);
      setSaveDialogOpen(false);
      toast({
        title: 'Success',
        description: projectName ? `Saved to "${projectName}"` : 'Document saved to project.',
      });
    } catch (error) {
      console.error('Error saving to project:', error);
      toast({
        title: 'Error',
        description: 'Failed to save document to project.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAndSave = async () => {
    if (!document || !createName.trim()) return;
    setSaving(true);
    try {
      const project = await projectsApi.create({ name: createName.trim() });
      await projectsApi.addDocument(project.id, document.id);
      void refreshProjectsCache();
      setSaveDialogOpen(false);
      setCreateName('');
      toast({
        title: 'Success',
        description: `Saved to new project "${project.name}".`,
      });
    } catch (error) {
      console.error('Error creating project and saving:', error);
      toast({
        title: 'Error',
        description: 'Failed to create project.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (loading || !document) {
      setBreadcrumbActions(null);
      return;
    }

    setBreadcrumbActions(
      <>
        <Button
          variant="outline"
          size={headerCompact ? 'icon' : 'sm'}
          onClick={openSaveDialog}
          className={cn('h-9', !headerCompact && 'gap-2 px-3')}
          aria-label="Save to project"
        >
          <Bookmark className="h-4 w-4" />
          {!headerCompact && <span>Save to project</span>}
        </Button>
        {document.source_url && (
          <Button
            variant="outline"
            size={headerCompact ? 'icon' : 'sm'}
            onClick={() => window.open(document.source_url, '_blank')}
            className={cn('h-9', !headerCompact && 'gap-2 px-3')}
            aria-label="View source"
          >
            <ExternalLink className="h-4 w-4" />
            {!headerCompact && <span>View source</span>}
          </Button>
        )}
      </>
    );

    return () => setBreadcrumbActions(null);
  }, [document, loading, setBreadcrumbActions, headerCompact]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!document) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <p className="text-muted-foreground">Document not found.</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto container-scroll">
        <article className="w-full max-w-3xl mx-auto px-4 py-6 md:py-8">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mb-6">
            {document.tags?.[0]?.label && (
              <span className="font-medium text-foreground/80">{document.tags[0].label}</span>
            )}
            <span>{document.category}</span>
            <span>
              Created{' '}
              {new Date(document.created_at).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight leading-tight mb-8 pb-6 border-b border-border/60">
            {document.title}
          </h1>

          <div className="w-full">
            {document.content?.trim() ? (
              <MarkdownMessage content={document.content} />
            ) : (
              <p className="text-sm text-muted-foreground">
                No content available for this document.
                {document.source_url ? ' Use "View source" to open the original page.' : ''}
              </p>
            )}
          </div>
        </article>
      </div>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-semibold">Save to project</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-3">Choose a project or create a new one.</p>
          {projectsLoading ? (
            <div className="flex justify-center py-4">
              <Spinner className="h-6 w-6" />
            </div>
          ) : (
            <div className="space-y-1">
              {projects.map((project) => (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => handleSaveToProject(project.id, project.name)}
                  disabled={saving}
                  className="w-full text-left p-3 rounded-lg hover:bg-accent transition-colors disabled:opacity-50"
                >
                  <div className="font-medium">{project.name}</div>
                  {project.description && (
                    <div className="text-sm text-muted-foreground">{project.description}</div>
                  )}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <Input
              placeholder="New project name"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleCreateAndSave} disabled={!createName.trim() || saving}>
              Create & save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
